/*
 * Scent Quiz — guided fragrance/layering finder.
 * Each answer adds a point to a "profile"; the highest-scoring profile wins and
 * shows its curated result (combo of products). Content is configured via the
 * section blocks in the theme customizer.
 */
class ScentQuiz extends HTMLElement {
  connectedCallback() {
    this.total = parseInt(this.dataset.total || '0', 10);
    this.redirect = this.dataset.redirect || 'cart';
    this.screens = Array.from(this.querySelectorAll('.scent-quiz__screen'));
    this.questions = Array.from(this.querySelectorAll('[data-screen="question"]'));
    this.results = Array.from(this.querySelectorAll('[data-screen="result"]'));
    this.scores = {};
    this.current = 0;

    const startBtn = this.querySelector('.scent-quiz__start');
    if (startBtn) startBtn.addEventListener('click', () => this.start());

    this.querySelectorAll('.scent-quiz__option').forEach((btn) => {
      btn.addEventListener('click', () => this.recordAndAdvance(btn.dataset.profile));
    });

    this.querySelectorAll('.scent-quiz__select').forEach((sel) => {
      sel.addEventListener('change', () => {
        const opt = sel.options[sel.selectedIndex];
        if (opt) this.recordAndAdvance(opt.dataset.profile);
      });
    });

    this.querySelectorAll('.scent-quiz__restart').forEach((btn) => {
      btn.addEventListener('click', () => this.restart());
    });

    this.querySelectorAll('.scent-quiz__add-all').forEach((btn) => {
      btn.addEventListener('click', () => this.addCombo(btn));
    });

    this.revealScreen = this.querySelector('[data-screen="reveal"]');
    const revealCard = this.querySelector('.scent-quiz__reveal-card');
    if (revealCard) revealCard.addEventListener('click', () => this.revealResult());

    this.teaserScreen = this.querySelector('[data-screen="teaser"]');

    // influencer capture: show the VIP form only when the page is opened with ?vip
    try {
      if (new URLSearchParams(window.location.search).has('vip')) this.classList.add('is-vip');
    } catch (e) { /* noop */ }

    // lead forms (teaser + influencer/VIP) — Shopify contact form submitted via AJAX (no reload)
    this.querySelectorAll('.scent-quiz__lead').forEach((leadForm) => {
      leadForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = leadForm.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('disabled', 'disabled');
        fetch(leadForm.action, {
          method: 'POST',
          body: new FormData(leadForm),
          headers: { Accept: 'application/json' },
        })
          .then(() => {
            leadForm.hidden = true;
            const wrap = leadForm.closest('.scent-quiz__vip') || leadForm.parentNode;
            const thanks = wrap.querySelector('.scent-quiz__lead-thanks');
            if (thanks) thanks.hidden = false;
          })
          .catch(() => {
            if (btn) btn.removeAttribute('disabled');
          });
      });
    });

    this.revealVideos = Array.from(this.querySelectorAll('.scent-quiz__reveal-video'));
    if (this.revealVideos.length) {
      this.revealVideos.forEach((v) => v.addEventListener('ended', () => this.revealResult()));
      // click/tap anywhere on the reveal screen skips to the result
      this.revealScreen.addEventListener('click', () => this.revealResult());
    }

    // result: reveal the products only when the user asks
    this.querySelectorAll('.scent-quiz__reveal-products').forEach((btn) => {
      btn.addEventListener('click', () => this.revealProducts(btn));
    });

    this.wordsearches = Array.from(this.querySelectorAll('.scent-quiz__wordsearch'));
    this.wordsearches.forEach((s) => this.buildWordsearch(s));

    this.memories = Array.from(this.querySelectorAll('.scent-quiz__memory'));
    this.memories.forEach((s) => this.buildMemory(s));

    // best-effort autoplay for the immersive background video.
    // iOS Low Power Mode (and some browsers) block muted autoplay → start it on the first tap.
    this.bgVideos = Array.from(this.querySelectorAll('video.scent-quiz__bg'));
    if (this.bgVideos.length) {
      const playBg = () => this.bgVideos.forEach((v) => {
        v.muted = true;
        const p = v.play();
        if (p && p.catch) p.catch(() => { /* will retry on user gesture */ });
      });
      playBg();
      const kick = () => {
        playBg();
        document.removeEventListener('touchstart', kick);
        document.removeEventListener('click', kick);
      };
      document.addEventListener('touchstart', kick, { passive: true });
      document.addEventListener('click', kick);
    }
  }

  // ----- Memory step (flip the cards, match the pairs) -----
  buildMemory(screen) {
    const grid = screen.querySelector('.scent-quiz__mem-grid');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.scent-quiz__mem-card'));
    const mem = {
      grid,
      cards,
      total: Math.floor(cards.length / 2),
      matched: 0,
      flipped: [],
      lock: false,
      countEl: screen.querySelector('.scent-quiz__mem-count'),
      continueBtn: screen.querySelector('.scent-quiz__mem-continue'),
    };
    screen._mem = mem;
    cards.forEach((card) => card.addEventListener('click', () => this.memFlip(screen, card)));
    if (mem.continueBtn) mem.continueBtn.addEventListener('click', () => this.recordAndAdvance(''));
    this.resetMemory(screen);
  }

  resetMemory(screen) {
    const mem = screen._mem;
    if (!mem) return;
    mem.matched = 0;
    mem.flipped = [];
    mem.lock = false;
    mem.cards.forEach((c) => c.classList.remove('is-flipped', 'is-matched'));
    this.memShuffle(mem);
    this.updateMem(screen);
  }

  memShuffle(mem) {
    const arr = mem.cards.slice();
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    arr.forEach((c) => mem.grid.appendChild(c)); // reorder DOM = reshuffle layout
  }

  memFlip(screen, card) {
    const mem = screen._mem;
    if (mem.lock) return;
    if (card.classList.contains('is-flipped') || card.classList.contains('is-matched')) return;

    card.classList.add('is-flipped');
    mem.flipped.push(card);
    if (mem.flipped.length < 2) return;

    mem.lock = true;
    const a = mem.flipped[0];
    const b = mem.flipped[1];
    if (a.dataset.pair === b.dataset.pair) {
      setTimeout(() => {
        a.classList.add('is-matched');
        b.classList.add('is-matched');
        mem.flipped = [];
        mem.lock = false;
        mem.matched += 1;
        this.updateMem(screen);
      }, 450);
    } else {
      setTimeout(() => {
        a.classList.remove('is-flipped');
        b.classList.remove('is-flipped');
        mem.flipped = [];
        mem.lock = false;
      }, 850);
    }
  }

  updateMem(screen) {
    const mem = screen._mem;
    if (mem.countEl) mem.countEl.textContent = mem.matched;
    if (mem.continueBtn) {
      if (mem.total > 0 && mem.matched >= mem.total) mem.continueBtn.removeAttribute('disabled');
      else mem.continueBtn.setAttribute('disabled', 'disabled');
    }
  }

  // ----- Word search step (tap the letters one by one) -----
  buildWordsearch(screen) {
    const grid = screen.querySelector('.scent-quiz__ws-grid');
    if (!grid) return;
    const cells = Array.from(grid.querySelectorAll('.scent-quiz__ws-cell'));
    const words = (grid.dataset.words || '')
      .split(',')
      .map((w) => w.trim().toUpperCase())
      .filter(Boolean);

    const ws = {
      grid, cells, words,
      findCount: parseInt(screen.dataset.find || '3', 10),
      found: new Set(),
      sel: [], // cells tapped so far, in order
      countEl: screen.querySelector('.scent-quiz__ws-count'),
      foundEl: screen.querySelector('.scent-quiz__ws-found'),
      continueBtn: screen.querySelector('.scent-quiz__ws-continue'),
    };
    screen._ws = ws;

    grid.addEventListener('click', (e) => {
      const cell = e.target.closest('.scent-quiz__ws-cell');
      if (cell) this.wsClick(screen, cell);
    });
    if (ws.continueBtn) ws.continueBtn.addEventListener('click', () => this.recordAndAdvance(''));
    this.resetWordsearch(screen);
  }

  resetWordsearch(screen) {
    const ws = screen._ws;
    if (!ws) return;
    ws.found.clear();
    ws.sel = [];
    ws.cells.forEach((c) => c.classList.remove('is-found', 'is-sel'));
    this.updateWs(screen);
  }

  wsClearSel(ws) {
    ws.sel.forEach((c) => { if (!c.classList.contains('is-found')) c.classList.remove('is-sel'); });
    ws.sel = [];
  }

  wsStartSel(ws, cell) {
    this.wsClearSel(ws);
    ws.sel = [cell];
    cell.classList.add('is-sel');
  }

  wsClick(screen, cell) {
    const ws = screen._ws;
    const r = parseInt(cell.dataset.r, 10);
    const c = parseInt(cell.dataset.c, 10);
    const last = ws.sel[ws.sel.length - 1];

    // tap the last letter again → undo it
    if (last === cell) {
      ws.sel.pop();
      cell.classList.remove('is-sel');
      return;
    }
    // tap a letter already in the selection (not the last) → ignore
    if (ws.sel.indexOf(cell) !== -1) return;

    if (ws.sel.length === 0) {
      this.wsStartSel(ws, cell);
    } else if (ws.sel.length === 1) {
      // second tap sets the direction: must be one of the 8 neighbours
      const a = ws.sel[0];
      const dr = r - parseInt(a.dataset.r, 10);
      const dc = c - parseInt(a.dataset.c, 10);
      if (Math.abs(dr) <= 1 && Math.abs(dc) <= 1) {
        ws.sel.push(cell);
        cell.classList.add('is-sel');
      } else {
        this.wsStartSel(ws, cell); // too far → start over here
      }
    } else {
      // must continue along the straight line already established
      const a = ws.sel[0];
      const dirR = Math.sign(parseInt(ws.sel[1].dataset.r, 10) - parseInt(a.dataset.r, 10));
      const dirC = Math.sign(parseInt(ws.sel[1].dataset.c, 10) - parseInt(a.dataset.c, 10));
      const expR = parseInt(last.dataset.r, 10) + dirR;
      const expC = parseInt(last.dataset.c, 10) + dirC;
      if (r === expR && c === expC) {
        ws.sel.push(cell);
        cell.classList.add('is-sel');
      } else {
        this.wsStartSel(ws, cell); // broke the line → start over here
      }
    }

    this.wsCheck(screen);
  }

  // auto-confirm when the tapped letters spell a target word
  wsCheck(screen) {
    const ws = screen._ws;
    if (ws.sel.length < 2) return;
    const word = ws.sel.map((c) => c.dataset.letter).join('');
    const rev = word.split('').reverse().join('');
    const match = ws.words.find((w) => (w === word || w === rev) && !ws.found.has(w));
    if (match) {
      ws.found.add(match);
      ws.sel.forEach((c) => { c.classList.remove('is-sel'); c.classList.add('is-found'); });
      ws.sel = [];
      this.updateWs(screen);
    }
  }

  updateWs(screen) {
    const ws = screen._ws;
    if (ws.countEl) ws.countEl.textContent = ws.found.size;
    if (ws.foundEl) {
      ws.foundEl.innerHTML = Array.from(ws.found)
        .map((w) => `<span class="scent-quiz__ws-chip">${w}</span>`)
        .join('');
    }
    if (ws.continueBtn) {
      if (ws.found.size >= ws.findCount) ws.continueBtn.removeAttribute('disabled');
      else ws.continueBtn.setAttribute('disabled', 'disabled');
    }
  }

  show(screen) {
    this.screens.forEach((s) => s.classList.remove('is-active', 'is-entering'));
    if (screen) {
      screen.classList.add('is-active');
      // force a reflow, then add .is-entering so the entrance animation replays every time
      void screen.offsetWidth;
      screen.classList.add('is-entering');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (screen === this.revealScreen) this.playReveal();
    }
  }

  // autoplay the VISIBLE reveal video (muted) when the reveal screen appears
  playReveal() {
    if (!this.revealVideos) return;
    this.revealVideos.forEach((vid) => {
      const visible = getComputedStyle(vid).display !== 'none';
      if (visible) {
        vid.muted = true;
        try { vid.currentTime = 0; } catch (e) { /* noop */ }
        const p = vid.play();
        if (p && p.catch) p.catch(() => { /* autoplay blocked → user taps to continue */ });
      } else {
        try { vid.pause(); } catch (e) { /* noop */ }
      }
    });
  }

  // tap "Scopri i tuoi prodotti" → switch from the personality page to the products page
  revealProducts(btn) {
    const result = btn.closest('.scent-quiz__result');
    if (!result) return;
    const pers = result.querySelector('.scent-quiz__result-personality');
    const prods = result.querySelector('.scent-quiz__result-products');
    if (pers) pers.hidden = true;
    if (prods) {
      prods.hidden = false;
      void prods.offsetWidth;
      prods.classList.add('is-revealing-combo');
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  start() {
    this.scores = {};
    this.current = 0;
    this._revealing = false;
    this.querySelectorAll('.scent-quiz__select').forEach((s) => { s.selectedIndex = 0; });
    if (this.wordsearches) this.wordsearches.forEach((s) => this.resetWordsearch(s));
    if (this.memories) this.memories.forEach((s) => this.resetMemory(s));
    if (this.revealScreen) this.revealScreen.classList.remove('is-opening', 'is-ending');
    // reset each result to its personality page (hide the products page)
    this.results.forEach((r) => {
      const pers = r.querySelector('.scent-quiz__result-personality');
      const prods = r.querySelector('.scent-quiz__result-products');
      if (pers) pers.hidden = false;
      if (prods) { prods.hidden = true; prods.classList.remove('is-revealing-combo'); }
    });
    if (this.questions.length) this.show(this.questions[0]);
    else this.finish();
  }

  recordAndAdvance(profile) {
    profile = (profile || '').trim().toLowerCase();
    if (profile) this.scores[profile] = (this.scores[profile] || 0) + 1;

    this.current += 1;
    if (this.current < this.questions.length) {
      this.show(this.questions[this.current]);
    } else if (this.revealScreen) {
      this.show(this.revealScreen);
    } else {
      this.finish();
    }
  }

  // Video ends (or user taps) → show the personality screen.
  revealResult() {
    if (this._revealing || !this.revealScreen) return;
    this._revealing = true;
    // VIDEO: hard cut straight to the result (no fade that would expose the quiz bg).
    if (this.revealVideos && this.revealVideos.length) {
      this.revealVideos.forEach((v) => { try { v.pause(); } catch (e) { /* noop */ } });
      this.finish();
      return;
    }
    // CARD fallback: keep the tap-to-unwrap animation.
    this.revealScreen.classList.add('is-opening');
    setTimeout(() => {
      this.revealScreen.classList.remove('is-opening');
      this._revealing = false;
      this.finish();
    }, 650);
  }

  finish() {
    // influencer/teaser mode: everyone sees the same "coming soon" screen, no result
    if (this.teaserScreen) {
      this.show(this.teaserScreen);
      return;
    }

    // highest score
    let max = -1;
    Object.values(this.scores).forEach((v) => {
      if (v > max) max = v;
    });

    // every result whose profile is tied at the top score
    let tied = this.results.filter((r) => {
      const p = (r.dataset.profile || '').trim().toLowerCase();
      return max > 0 && (this.scores[p] || 0) === max;
    });
    // no answer scored (or no match) → any result is fair game
    if (!tied.length) tied = this.results.slice();

    // pick RANDOMLY among the tied leaders so combinations actually vary
    const winner = tied[Math.floor(Math.random() * tied.length)];
    if (winner) {
      winner.classList.add('is-revealing');
      setTimeout(() => winner.classList.remove('is-revealing'), 1200);
    }
    this.show(winner);
  }

  restart() {
    this.start();
  }

  addCombo(btn) {
    const ids = (btn.dataset.variants || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) return;

    btn.setAttribute('disabled', 'disabled');
    const items = ids.map((id) => ({ id: parseInt(id, 10), quantity: 1 }));

    fetch(`${theme.routes.cart_add_url}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json())
      .then(() => {
        if (this.redirect === 'cart') {
          window.location.href = theme.routes.cart_url;
        } else {
          btn.removeAttribute('disabled');
          btn.textContent = '✓ Aggiunta!';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      })
      .catch((e) => {
        console.error('[ScentQuiz]', e);
        btn.removeAttribute('disabled');
      });
  }
}

customElements.define('scent-quiz', ScentQuiz);
